// FOC subscriptions modal logic – runs on all pages where #subs-modal exists
document.addEventListener('DOMContentLoaded', () => {
  const getModal = () => document.getElementById('subs-modal');

  // ---------- helpers ----------
  function shallowEqualCI(a = [], b = []) {
    const A = Array.from(new Set(a.map(x => String(x).toLowerCase()))).sort();
    const B = Array.from(new Set(b.map(x => String(x).toLowerCase()))).sort();
    if (A.length !== B.length) return false;
    for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
    return true;
  }

  function setSavingState(btn, isSaving) {
    if (!btn) return;
    if (isSaving) {
      btn.dataset._orig = btn.textContent;
      btn.textContent = 'Saving…';
      btn.disabled = true;
    } else {
      btn.textContent = btn.dataset._orig || 'Save to My FOC';
      btn.disabled = false;
    }
  }

  const toArray = (x) => {
    if (!x) return [];
    try {
      const parsed = typeof x === 'string' ? JSON.parse(x) : x;
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string' && parsed.trim()) return [parsed.trim()];
      return [];
    } catch {
      return (typeof x === 'string' && x.trim()) ? [x.trim()] : [];
    }
  };

  const parseMaybeJson = (s) => {
    if (typeof s !== 'string') return s ?? '';
    try {
      const v = JSON.parse(s);
      return (typeof v === 'string') ? v : s;
    } catch {
      return s;
    }
  };

  const clean = (s) => String(s || '').replace(/^"+|"+$/g, '').trim();
  const uniq = (arr) =>
    Array.from(new Set((Array.isArray(arr) ? arr : []).map(v => String(v).trim()).filter(Boolean)));

  const hasCI = (arr, val) =>
    (Array.isArray(arr) ? arr : [])
      .map(String).map(s => s.toLowerCase())
      .includes(String(val || '').toLowerCase());

  // Title case (for names)
  function titleCase(str) {
    return String(str || '')
      .toLowerCase()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Name formatting
  function toFirstLast(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (s.includes(',')) {
      const [last, first] = s.split(',').map(x => x.trim()).filter(Boolean);
      return [first, last].filter(Boolean).join(' ');
    }
    return s;
  }

  function toLastFirst(firstLast) {
    const parts = String(firstLast || '').trim().split(/\s+/);
    if (parts.length <= 1) return firstLast || '';
    const last = parts.pop();
    return `${last}, ${parts.join(' ')}`.trim();
  }

  function valuesForCreator(raw) {
    const fl = toFirstLast(raw);
    const lf = toLastFirst(fl);
    return Array.from(new Set([raw, fl, lf].map(v => String(v || '').trim()).filter(Boolean)));
  }

  function anyCI(haystack, needles) {
    const H = (haystack || []).map(s => String(s).toLowerCase());
    return (needles || []).some(n => H.includes(String(n || '').toLowerCase()));
  }

  // Toggle factory — supports multiple stored values
  function makeToggle(idBase, labelText, checked = true, values = [labelText]) {
    const row = document.createElement('div');
    row.className = 'foc-toggle-row';

    const lbl = document.createElement('label');
    lbl.className = 'foc-toggle';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'subs-toggle';
    input.id = `${idBase}-${Math.random().toString(36).slice(2, 7)}`;
    input.checked = !!checked;
    input.dataset.values = JSON.stringify(values);

    const slider = document.createElement('span');
    slider.className = 'foc-slider';

    const text = document.createElement('label');
    text.className = 'foc-toggle-label';
    text.setAttribute('for', input.id);
    text.textContent = labelText;

    lbl.appendChild(input);
    lbl.appendChild(slider);
    row.appendChild(lbl);
    row.appendChild(text);
    return row;
  }

  async function openModalFromBtn(btn) {
    if (!window.__customerLoggedIn) {
      // Use routes object we exposed in theme.liquid
      const loginUrl = (window.routes && window.routes.accountLoginUrl) || '/account/login';
      window.location.href = loginUrl;
      return;
    }

    const pt = (btn.dataset.productType || '').toLowerCase();
    if (!(pt.includes('comic') || pt.includes('graphic'))) return;

    let modal = getModal();
    if (!modal) {
      await new Promise(r => setTimeout(r, 0));
      modal = getModal();
    }
    if (!modal) return;

    const dialog = modal.querySelector('.subs-modal__dialog');
    const backdrop = modal.querySelector('.subs-modal__backdrop');
    const wrapSeries = modal.querySelector('#subs-series');
    const wrapCreators = modal.querySelector('#subs-creators');
    const wrapKeywords = modal.querySelector('#subs-keywords');
    const btnSave = modal.querySelector('#subs-save');
    if (!dialog || !backdrop || !wrapSeries || !wrapCreators || !wrapKeywords || !btnSave) return;

    dialog.addEventListener('click', (e) => e.stopPropagation());
    backdrop.addEventListener('click', () => closeModal(modal));
    modal.querySelectorAll('.subs-close').forEach(x =>
      x.addEventListener('click', () => closeModal(modal))
    );

    wrapSeries.innerHTML = '';
    wrapCreators.innerHTML = '';
    wrapKeywords.innerHTML = '';

    const seriesCodes = uniq(toArray(btn.dataset.seriesCodes));
    const seriesName = clean(parseMaybeJson(btn.dataset.seriesName));
    const creatorsRaw = uniq(toArray(btn.dataset.creators));
    const keywords = uniq(toArray(btn.dataset.keywords));

    const existing = window.customerSubscriptions || {};
    const sExisting = Array.isArray(existing.series_codes) ? existing.series_codes : [];
    const cExisting = Array.isArray(existing.cover_artists) ? existing.cover_artists : [];
    const kExisting = Array.isArray(existing.keywords) ? existing.keywords : [];

    // Series (store NAME only; ignore codes completely)
    const seriesDisplay = seriesName; // do not fall back to codes
    if (seriesDisplay) {
      const seriesValues = [seriesDisplay]; // only the name is stored/checked
      const preselect = anyCI(sExisting, seriesValues);
      wrapSeries.appendChild(makeToggle('series', seriesDisplay, preselect, seriesValues));
    }

    // Creators
    const creatorMap = new Map();
    creatorsRaw.forEach(raw => {
      const fl = titleCase(toFirstLast(raw));
      if (!fl) return;
      const canon = fl.toLowerCase();
      const values = valuesForCreator(raw);
      if (creatorMap.has(canon)) {
        const entry = creatorMap.get(canon);
        entry.values = Array.from(new Set([...entry.values, ...values]));
      } else {
        creatorMap.set(canon, { label: fl, values });
      }
    });
    creatorMap.forEach(({ label, values }) => {
      const pre = anyCI(cExisting, values);
      wrapCreators.appendChild(makeToggle('creator', label, pre, values));
    });

    // Keywords
    keywords.forEach(k => {
      const label = String(k || '').trim();
      if (!label) return;
      const pre = hasCI(kExisting, label);
      wrapKeywords.appendChild(makeToggle('keyword', label, pre, [label]));
    });

    // Compute initial snapshot to check for changes later
    const initialSnapshot = {
      series: Array.from(wrapSeries.querySelectorAll('.subs-toggle:checked'))
        .flatMap(inp => {
          try { return JSON.parse(inp.dataset.values) || []; }
          catch { return []; }
        }),
      creators: Array.from(wrapCreators.querySelectorAll('.subs-toggle:checked'))
        .flatMap(inp => {
          try { return JSON.parse(inp.dataset.values) || []; }
          catch { return []; }
        }),
      keywords: Array.from(wrapKeywords.querySelectorAll('.subs-toggle:checked'))
        .flatMap(inp => {
          try { return JSON.parse(inp.dataset.values) || []; }
          catch { return []; }
        })
    };

    function currentSnapshot() {
      const pick = (root) =>
        Array.from(root.querySelectorAll('.subs-toggle:checked'))
          .flatMap(inp => {
            try { return JSON.parse(inp.dataset.values) || []; }
            catch { return []; }
          });
      return { series: pick(wrapSeries), creators: pick(wrapCreators), keywords: pick(wrapKeywords) };
    }

    function isDirty() {
      const cur = currentSnapshot();
      return !(
        shallowEqualCI(cur.series, initialSnapshot.series) &&
        shallowEqualCI(cur.creators, initialSnapshot.creators) &&
        shallowEqualCI(cur.keywords, initialSnapshot.keywords)
      );
    }

    // Disable save until something changes
    btnSave.disabled = !isDirty();

    // Recompute on any toggle change
    modal.querySelectorAll('.subs-toggle').forEach(i => {
      i.addEventListener('change', () => {
        btnSave.disabled = !isDirty();
      });
    });

    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    btnSave.onclick = async () => {
      if (!isDirty()) return;

      // Pull values off a toggle; fallback to its label if dataset is missing
      const parseValues = (inp) => {
        try { return JSON.parse(inp.dataset.values) || []; }
        catch {
          const label = inp.parentElement?.nextElementSibling?.textContent?.trim();
          return label ? [label] : [];
        }
      };

      // Build { value: true|false, ... } for ALL toggles in a section
      const buildValueMap = (root) => {
        const map = Object.create(null);
        root.querySelectorAll('.subs-toggle').forEach(inp => {
          const checked = !!inp.checked;
          const vals = parseValues(inp);
          vals.forEach(v => {
            const key = String(v || '').trim();
            if (!key) return;
            map[key] = checked; // last write wins
          });
        });
        return map;
      };

      const wrapSeriesEl = wrapSeries;
      const wrapCreatorsEl = wrapCreators;
      const wrapKeywordsEl = wrapKeywords;

      const seriesMap = buildValueMap(wrapSeriesEl);     // name
      const contributorsMap = buildValueMap(wrapCreatorsEl); // First Last + Last, First
      const keywordsMap = buildValueMap(wrapKeywordsEl); // words

      // New explicit payload (maps of value => boolean)
      const payload = {
        series: seriesMap,
        contributors: contributorsMap,
        keywords: keywordsMap
      };

      const send = async () => {
        const res = await fetch('/apps/popwdata/alerts/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          if (res.status === 401) {
            alert('Please sign in to save your alerts.');
            const loginUrl = (window.routes && window.routes.accountLoginUrl) || '/account/login';
            window.location.href = loginUrl + '?return_url=' + encodeURIComponent(location.pathname + location.search);
          }
          throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
        }
        return res.json().catch(() => ({}));
      };

      try {
        setSavingState(btnSave, true);

        let resp;
        try { resp = await send(); }
        catch {
          await new Promise(r => setTimeout(r, 500));
          resp = await send();
        }

        // Optimistically update client cache using what's ON
        window.customerSubscriptions = {
          ...(window.customerSubscriptions || {}),
          series_codes: Object.keys(seriesMap).filter(k => seriesMap[k]),
          cover_artists: Object.keys(contributorsMap).filter(k => contributorsMap[k]),
          keywords: Object.keys(keywordsMap).filter(k => keywordsMap[k])
        };

        btnSave.textContent = 'Saved!';
        setTimeout(() => {
          btnSave.textContent = 'Save to My FOC';
          btnSave.disabled = true;
          closeModal(modal);
        }, 800);
      } catch (e) {
        console.error('Save failed', e);
        btnSave.textContent = 'Retry Save';
        btnSave.disabled = false;
        alert('Could not save your preferences. Please try again.');
      } finally {
        if (btnSave.textContent !== 'Saved!') setSavingState(btnSave, false);
      }
    };
  }

  function closeModal(modal) {
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  // Global click handler to open modal from any .subs-open button
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.subs-open');
    if (btn) {
      e.preventDefault();
      openModalFromBtn(btn);
    }
  });
});
